import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Loader2, Search, Shield, Wrench, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_CONFIG = {
    engineer: { label: 'Engineer', icon: Wrench, color: 'bg-blue-100 text-blue-700' },
    admin: { label: 'Admin', icon: Shield, color: 'bg-amber-100 text-amber-700' },
    global_admin: { label: 'Global Admin', icon: ShieldCheck, color: 'bg-purple-100 text-purple-700' },
};

const UserManagement = () => {
    const { token, user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'engineer' });

    const isGlobalAdmin = currentUser?.role === 'global_admin';

    const availableRoles = isGlobalAdmin
        ? ['engineer', 'admin', 'global_admin']
        : ['engineer', 'admin'];

    useEffect(() => { fetchUsers(); }, []);

    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
            setUsers(res.data);
        } catch { toast.error('Failed to load users'); }
        finally { setLoading(false); }
    };

    const handleOpenDialog = (user = null) => {
        if (user) {
            setEditingUser(user);
            setForm({ name: user.name, email: user.email, password: '', role: user.role });
        } else {
            setEditingUser(null);
            setForm({ name: '', email: '', password: '', role: 'engineer' });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
        if (!editingUser && !form.password) { toast.error('Password is required for new users'); return; }

        setSaving(true);
        try {
            if (editingUser) {
                const payload = { name: form.name, email: form.email, role: form.role };
                if (form.password) payload.password = form.password;
                await axios.put(`${API}/admin/users/${editingUser.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('User updated');
            } else {
                await axios.post(`${API}/admin/users`, form, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('User created');
            }
            setDialogOpen(false);
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save user');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!userToDelete) return;
        setSaving(true);
        try {
            await axios.delete(`${API}/admin/users/${userToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('User deleted');
            setDeleteConfirmOpen(false);
            setUserToDelete(null);
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete user');
        } finally { setSaving(false); }
    };

    const filteredUsers = users.filter(u =>
        u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-brand-lime" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-5xl mx-auto" data-testid="user-management-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-brand-charcoal">User Management</h1>
                    <p className="text-sm text-brand-gray-500 mt-1">{users.length} user{users.length !== 1 ? 's' : ''}</p>
                </div>
                <Button onClick={() => handleOpenDialog()} className="bg-[#7AC943] hover:bg-[#68B035] text-black font-bold" data-testid="add-user-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add User
                </Button>
            </div>

            <Card className="border-0 shadow-lg rounded-lg overflow-hidden">
                <CardHeader className="pb-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                        <Input type="text" placeholder="Search users..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="h-11 pl-10" data-testid="user-search" />
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {filteredUsers.length === 0 ? (
                        <div className="text-center py-12">
                            <Users className="h-10 w-10 mx-auto text-brand-gray-300 mb-2" />
                            <p className="text-brand-gray-500">No users found</p>
                        </div>
                    ) : (
                        <>
                            {/* Mobile */}
                            <div className="md:hidden divide-y divide-brand-gray-100">
                                {filteredUsers.map(u => {
                                    const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.engineer;
                                    const isSelf = u.id === currentUser?.id;
                                    return (
                                        <div key={u.id} className="px-4 py-3" data-testid={`user-${u.id}`}>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-brand-charcoal">{u.name}</p>
                                                    <p className="text-xs text-brand-gray-500 mt-0.5">{u.email}</p>
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${rc.color}`}>
                                                        <rc.icon className="h-3 w-3" />{rc.label}
                                                    </span>
                                                </div>
                                                <div className="flex gap-1">
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenDialog(u)} data-testid={`edit-user-${u.id}`}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    {!isSelf && (
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => { setUserToDelete(u); setDeleteConfirmOpen(true); }} data-testid={`delete-user-${u.id}`}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {/* Desktop */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th className="text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.map(u => {
                                            const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.engineer;
                                            const isSelf = u.id === currentUser?.id;
                                            return (
                                                <tr key={u.id} data-testid={`user-desktop-${u.id}`}>
                                                    <td className="font-medium">{u.name}</td>
                                                    <td className="text-brand-gray-500">{u.email}</td>
                                                    <td>
                                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${rc.color}`}>
                                                            <rc.icon className="h-3 w-3" />{rc.label}
                                                        </span>
                                                    </td>
                                                    <td className="text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenDialog(u)} data-testid={`edit-user-${u.id}`}>
                                                                <Pencil className="h-4 w-4" />
                                                            </Button>
                                                            {!isSelf && (
                                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => { setUserToDelete(u); setDeleteConfirmOpen(true); }} data-testid={`delete-user-${u.id}`}>
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-[420px]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
                        <DialogDescription>{editingUser ? 'Update user details' : 'Create a new user account'}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide">Name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="h-11" data-testid="user-name-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide">Email</Label>
                            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@company.com" className="h-11" data-testid="user-email-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide">{editingUser ? 'New Password (leave blank to keep)' : 'Password'}</Label>
                            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editingUser ? 'Leave blank to keep current' : 'Password'} className="h-11" data-testid="user-password-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide">Role</Label>
                            <Select value={form.role} onValueChange={(value) => setForm({ ...form, role: value })}>
                                <SelectTrigger className="h-11" data-testid="user-role-select">
                                    <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableRoles.map(role => (
                                        <SelectItem key={role} value={role}>{ROLE_CONFIG[role].label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} className="bg-[#7AC943] hover:bg-[#68B035] text-black" disabled={saving} data-testid="save-user-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm */}
            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Delete User</DialogTitle>
                        <DialogDescription>Are you sure you want to delete "{userToDelete?.name}"? This action cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-user-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UserManagement;
